import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Gestor {
  id: string;
  nome: string;
  telefone?: string;
  whatsapp?: string;
  email?: string;
  rg?: string;
  cpf?: string;
  ordem: number;
}

interface Cessionario {
  id: string;
  nome_completo: string;
  telefone?: string;
  whatsapp?: string;
  email?: string;
  rg?: string;
  cpf?: string;
  ordem: number;
}

export function useGestorCessionarioList(clienteId?: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Query para gestores
  const { data: gestores = [], isLoading: isLoadingGestores } = useQuery({
    queryKey: ['cliente-contatos', clienteId],
    queryFn: async () => {
      if (!clienteId) return [];
      
      const { data, error } = await supabase
        .from('cliente_contato')
        .select('*')
        .eq('cliente_id', clienteId)
        .order('ordem');

      if (error) throw error;
      return (data || []) as Gestor[];
    },
    enabled: !!clienteId,
  });

  // Query para cessionários
  const { data: cessionarios = [], isLoading: isLoadingCessionarios } = useQuery({
    queryKey: ['cliente-cessionarios', clienteId],
    queryFn: async () => {
      if (!clienteId) return [];
      
      const { data, error } = await supabase
        .from('cliente_cessionario')
        .select('*')
        .eq('cliente_id', clienteId)
        .order('ordem');

      if (error) throw error;
      return (data || []) as Cessionario[];
    },
    enabled: !!clienteId,
  });

  // Adicionar gestor
  const addGestorMutation = useMutation({
    mutationFn: async (data: Partial<Gestor>) => {
      // Se não há gestores, este será o primeiro (ordem = 0)
      // Caso contrário, pega o próximo número da ordem
      const maxOrdem = gestores.length > 0 ? Math.max(...gestores.map(g => g.ordem)) : -1;
      const novaOrdem = gestores.length === 0 ? 0 : maxOrdem + 1;
      
      const { id, ...dataWithoutId } = data as any;
      
      const { error } = await supabase
        .from('cliente_contato')
        .insert([{
          cliente_id: clienteId!,
          ...dataWithoutId,
          ordem: novaOrdem,
        }]);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cliente-contatos', clienteId] });
      toast({ title: 'Gestor adicionado com sucesso' });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro ao adicionar gestor', description: error.message, variant: 'destructive' });
    },
  });

  // Atualizar gestor
  const updateGestorMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Gestor> }) => {
      const { error } = await supabase
        .from('cliente_contato')
        .update(data)
        .eq('id', id);

      if (error) throw error;
      return id; // Retornar ID para usar no onSuccess
    },
    onSuccess: (gestorId) => {
      // ✅ Invalidar queryKey CORRETA de leitura (gestor-completo)
      queryClient.invalidateQueries({ queryKey: ['gestor-completo'], exact: false });
      // ✅ Invalidar TODAS as queries relacionadas ao gestor
      queryClient.invalidateQueries({ queryKey: ['cliente-contatos', clienteId] });
      queryClient.invalidateQueries({ queryKey: ['gestor', gestorId] });
      queryClient.invalidateQueries({ queryKey: ['gestor'] }); // Todas queries de gestor
      queryClient.invalidateQueries({ queryKey: ['pedido-detail'] });
      queryClient.invalidateQueries({ queryKey: ['pedido-itens-completo'] });
      queryClient.invalidateQueries({ queryKey: ['kanban'], exact: false });
      // ✅ Invalidar item-completo para atualizar validação imediatamente
      queryClient.invalidateQueries({ queryKey: ['item-completo'], exact: false });
      toast({ title: 'Gestor atualizado com sucesso' });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro ao atualizar gestor', description: error.message, variant: 'destructive' });
    },
  });

  // Remover gestor
  const removeGestorMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('cliente_contato')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cliente-contatos', clienteId] });
      toast({ title: 'Gestor removido com sucesso' });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro ao remover gestor', description: error.message, variant: 'destructive' });
    },
  });

  // Adicionar cessionário
  const addCessionarioMutation = useMutation({
    mutationFn: async (data: Partial<Cessionario>) => {
      // Se não há cessionários, este será o primeiro (ordem = 0)
      // Caso contrário, pega o próximo número da ordem
      const maxOrdem = cessionarios.length > 0 ? Math.max(...cessionarios.map(c => c.ordem)) : -1;
      const novaOrdem = cessionarios.length === 0 ? 0 : maxOrdem + 1;
      
      const { id, ...dataWithoutId } = data as any;
      
      const { error } = await supabase
        .from('cliente_cessionario')
        .insert([{
          cliente_id: clienteId!,
          ...dataWithoutId,
          ordem: novaOrdem,
        }]);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cliente-cessionarios', clienteId] });
      toast({ title: 'Cessionário adicionado com sucesso' });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro ao adicionar cessionário', description: error.message, variant: 'destructive' });
    },
  });

  // Atualizar cessionário
  const updateCessionarioMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Cessionario> }) => {
      const { error } = await supabase
        .from('cliente_cessionario')
        .update(data)
        .eq('id', id);

      if (error) throw error;
      return id; // Retornar ID para usar no onSuccess
    },
    onSuccess: (cessionarioId) => {
      // ✅ Invalidar queryKey CORRETA de leitura (cessionario-completo)
      queryClient.invalidateQueries({ queryKey: ['cessionario-completo'], exact: false });
      // ✅ Invalidar TODAS as queries relacionadas ao cessionário
      queryClient.invalidateQueries({ queryKey: ['cliente-cessionarios', clienteId] });
      queryClient.invalidateQueries({ queryKey: ['cessionario', cessionarioId] });
      queryClient.invalidateQueries({ queryKey: ['cessionario'] }); // Todas queries de cessionário
      queryClient.invalidateQueries({ queryKey: ['pedido-detail'] });
      queryClient.invalidateQueries({ queryKey: ['pedido-itens-completo'] });
      queryClient.invalidateQueries({ queryKey: ['kanban'], exact: false });
      // ✅ Invalidar item-completo para atualizar validação imediatamente
      queryClient.invalidateQueries({ queryKey: ['item-completo'], exact: false });
      toast({ title: 'Cessionário atualizado com sucesso' });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro ao atualizar cessionário', description: error.message, variant: 'destructive' });
    },
  });

  // Remover cessionário
  const removeCessionarioMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('cliente_cessionario')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cliente-cessionarios', clienteId] });
      toast({ title: 'Cessionário removido com sucesso' });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro ao remover cessionário', description: error.message, variant: 'destructive' });
    },
  });

  return {
    gestores,
    cessionarios,
    isLoading: isLoadingGestores || isLoadingCessionarios,
    addGestor: addGestorMutation.mutateAsync,
    updateGestor: updateGestorMutation.mutateAsync,
    removeGestor: removeGestorMutation.mutateAsync,
    addCessionario: addCessionarioMutation.mutateAsync,
    updateCessionario: updateCessionarioMutation.mutateAsync,
    removeCessionario: removeCessionarioMutation.mutateAsync,
  };
}
